import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Save, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BriefEditor } from "./BriefEditor";
import type { BriefEditorContextValue } from "./briefEditorContext";
import { createBrief, type ContentBrief } from "@/lib/composerCore";
import { createUserEditRevision, saveUserEditRevision } from "@/lib/briefRevisions";
import { composerBus } from "@/lib/composerBus";
import { docToTitle, emptyDoc, isMeaningful, storageKey } from "./draftCanvasUtils";
import { useCanvasStream, type StreamTarget } from "./useCanvasStream";
import { StreamingBanner } from "./StreamingBanner";



type BlankCanvasProps = {
  workspaceId: string;
  existingBrief: ContentBrief | null;
  onPromoted?: () => void;
};

export const BlankCanvas = ({ workspaceId, existingBrief, onPromoted }: BlankCanvasProps) => {
  const briefId = existingBrief?.id ?? null;
  const key = storageKey(workspaceId, briefId);
  const titleKey = `${key}::title`;

  const [doc, setDoc] = useState<unknown>(() => {
    if (typeof window === "undefined") return emptyDoc;
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as unknown) : emptyDoc;
    } catch {
      return emptyDoc;
    }
  });
  const [title, setTitle] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(titleKey) ?? "";
  });
  const [saving, setSaving] = useState(false);

  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      try {
        const raw = window.localStorage.getItem(key);
        setDoc(raw ? (JSON.parse(raw) as unknown) : emptyDoc);
        setTitle(window.localStorage.getItem(titleKey) ?? "");
      } catch {
        setDoc(emptyDoc);
        setTitle("");
      }
    }
  }, [key, titleKey]);

  const ctx: BriefEditorContextValue = useMemo(
    () => ({ workspaceId, briefId: briefId ?? "blank", references: [] }),
    [workspaceId, briefId],
  );

  const handleChange = useCallback(
    (json: unknown) => {
      setDoc(json);
      try {
        window.localStorage.setItem(key, JSON.stringify(json));
      } catch {
        /* quota — ignore */
      }
    },
    [key],
  );

  const handleTitleChange = (value: string) => {
    setTitle(value);
    try {
      if (value) window.localStorage.setItem(titleKey, value);
      else window.localStorage.removeItem(titleKey);
    } catch {
      /* noop */
    }
  };

  const placeholderTitle = docToTitle(doc);
  const canSave = isMeaningful(doc) && !saving;

  const handleSaveAsDraft = useCallback(async () => {
    if (!canSave) {
      toast.message("Type something into the canvas first.");
      return;
    }
    setSaving(true);
    try {
      const finalTitle = title.trim() || placeholderTitle || "Untitled draft";
      let activeBrief = existingBrief;
      if (!activeBrief) activeBrief = await createBrief(workspaceId, { title: finalTitle });
      const revision = await createUserEditRevision({ workspaceId, briefId: activeBrief.id, source: null });
      await saveUserEditRevision({ draftId: revision.id, doc, title: finalTitle });
      try {
        window.localStorage.removeItem(key);
        window.localStorage.removeItem(titleKey);
      } catch {
        /* noop */
      }
      toast.success(existingBrief ? "Draft saved" : "Piece + draft created");
      onPromoted?.();
    } catch (err) {
      toast.error((err as Error).message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  }, [canSave, doc, existingBrief, key, onPromoted, placeholderTitle, title, titleKey, workspaceId]);

  // Subscribe to draft:insert (replace-clean) — Templates & Blueprints push here.
  // Auto-stash dirty canvas content as a Draft first, then load the new doc.
  useEffect(() => {
    return composerBus.subscribe("draft:insert", async (payload) => {
      if (payload.mode !== "replace-clean") return;
      const dirty = isMeaningful(doc) || title.trim().length > 0;
      try {
        if (dirty) {
          const finalTitle = title.trim() || placeholderTitle || "Untitled draft";
          let stashBrief = existingBrief;
          if (!stashBrief) stashBrief = await createBrief(workspaceId, { title: finalTitle });
          const rev = await createUserEditRevision({ workspaceId, briefId: stashBrief.id, source: null });
          await saveUserEditRevision({ draftId: rev.id, doc, title: finalTitle });
          toast.success("Stashed your work as a Draft", {
            description: `Loading "${payload.label ?? "new content"}" into a clean canvas.`,
          });
          onPromoted?.();
        } else {
          toast.success(`Loaded "${payload.label ?? "content"}" into the canvas`);
        }
        setDoc(payload.doc);
        setTitle(payload.label ?? "");
        try {
          window.localStorage.setItem(key, JSON.stringify(payload.doc));
          if (payload.label) window.localStorage.setItem(titleKey, payload.label);
          else window.localStorage.removeItem(titleKey);
        } catch {
          /* quota — ignore */
        }
      } catch (err) {
        toast.error((err as Error).message || "Push to canvas failed");
      }
    });
  }, [doc, existingBrief, key, onPromoted, placeholderTitle, title, titleKey, workspaceId]);

  // Subscribe to piece:open — auto-stash dirty blank canvas, then ack so
  // Composer can flip the active piece.
  useEffect(() => {
    return composerBus.subscribe("piece:open", async (payload) => {
      if (existingBrief?.id === payload.pieceId) {
        composerBus.emit("piece:open:ack", { pieceId: payload.pieceId, stashed: false });
        return;
      }
      const dirty = isMeaningful(doc) || title.trim().length > 0;
      let stashed = false;
      try {
        if (dirty) {
          const finalTitle = title.trim() || placeholderTitle || "Untitled draft";
          let stashBrief = existingBrief;
          if (!stashBrief) stashBrief = await createBrief(workspaceId, { title: finalTitle });
          const rev = await createUserEditRevision({ workspaceId, briefId: stashBrief.id, source: null });
          await saveUserEditRevision({ draftId: rev.id, doc, title: finalTitle });
          stashed = true;
          try {
            window.localStorage.removeItem(key);
            window.localStorage.removeItem(titleKey);
          } catch {
            /* noop */
          }
          setDoc(emptyDoc);
          setTitle("");
          onPromoted?.();
        }
        toast.success(`Opened "${payload.label ?? "piece"}"`, {
          description: stashed ? "Your previous work is in the Drafts tab." : undefined,
        });
        composerBus.emit("piece:open:ack", { pieceId: payload.pieceId, stashed });
      } catch (err) {
        toast.error((err as Error).message || "Failed to open piece");
      }
    });
  }, [doc, existingBrief, key, onPromoted, placeholderTitle, title, titleKey, workspaceId]);

  const handleClear = () => {
    if (isMeaningful(doc) || title.trim()) {
      const ok = window.confirm("Clear this blank canvas? Anything you typed here will be lost.");
      if (!ok) return;
    }
    try {
      window.localStorage.removeItem(key);
      window.localStorage.removeItem(titleKey);
    } catch {
      /* noop */
    }
    setDoc(emptyDoc);
    setTitle("");
  };

  // Per-token canvas streaming from the AI Chat "Write to canvas" toggle.
  // Append/Replace fall back to "new" here since there's no draft yet to
  // append to or replace.
  const handleStreamStart = useCallback(
    async ({ label }: { label: string; mode: "new" | "append" | "replace" }): Promise<StreamTarget> => {
      // Stash any dirty work first so the user never loses their canvas.
      const dirty = isMeaningful(doc) || title.trim().length > 0;
      let activeBrief = existingBrief;
      if (dirty) {
        const finalTitle = title.trim() || placeholderTitle || "Untitled draft";
        if (!activeBrief) activeBrief = await createBrief(workspaceId, { title: finalTitle });
        const stash = await createUserEditRevision({
          workspaceId,
          briefId: activeBrief.id,
          source: null,
        });
        await saveUserEditRevision({ draftId: stash.id, doc, title: finalTitle });
      }
      if (!activeBrief) activeBrief = await createBrief(workspaceId, { title: label });
      const target = await createUserEditRevision({
        workspaceId,
        briefId: activeBrief.id,
        source: null,
      });
      // Clear local canvas state — we're now streaming into a real draft.
      try {
        window.localStorage.removeItem(key);
        window.localStorage.removeItem(titleKey);
      } catch {
        /* noop */
      }
      setDoc(emptyDoc);
      setTitle("");
      if (dirty) {
        toast.success("Stashed your work as a Draft", {
          description: "Excerptus AI is streaming into a fresh draft now.",
        });
      }
      return { draftId: target.id, workspaceId, briefId: activeBrief.id, label, mode: "new" };
    },
    [doc, existingBrief, key, placeholderTitle, title, titleKey, workspaceId],
  );

  const handleStreamEnd = useCallback(
    async ({ target, doc: finalDoc }: { target: StreamTarget; doc: unknown; aborted: boolean }) => {
      await saveUserEditRevision({
        draftId: target.draftId,
        doc: finalDoc,
        title: target.label,
      });
      toast.success("Excerptus AI draft saved");
      onPromoted?.();
    },
    [onPromoted],
  );

  const stream = useCanvasStream({
    enabled: Boolean(workspaceId),
    onStart: handleStreamStart,
    onEnd: handleStreamEnd,
  });

  const { settling, acknowledge } = stream;
  useEffect(() => {
    if (!settling) return;
    const t = window.setTimeout(() => acknowledge(), 6000);
    return () => window.clearTimeout(t);
  }, [settling, acknowledge]);

  const streamCtx: BriefEditorContextValue = useMemo(
    () => ({
      workspaceId,
      briefId: existingBrief?.id ?? stream.target?.briefId ?? "blank",
      references: [],
    }),
    [workspaceId, existingBrief?.id, stream.target?.briefId],
  );



  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={placeholderTitle || "Untitled draft"}
          className="h-9 basis-full min-w-0 border-transparent bg-transparent px-2 text-base font-semibold shadow-none focus-visible:border-input focus-visible:bg-background md:basis-auto md:flex-1 md:max-w-md"
          aria-label="Draft title"
        />
        <span className="order-3 ml-auto text-xs text-muted-foreground md:order-none md:ml-0">Saved locally</span>
        <div className="order-2 ml-auto flex flex-wrap items-center justify-end gap-1.5 md:order-none">
          {isMeaningful(doc) || title.trim() ? (
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          ) : null}
          <Button size="sm" variant="default" disabled={!canSave} onClick={handleSaveAsDraft}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save as Draft"}
          </Button>
        </div>
      </div>
      {stream.active ? <StreamingBanner label={stream.target?.label ?? "Excerptus AI draft"} /> : null}
      <div className="min-h-0 flex-1">
        {stream.active ? (
          <BriefEditor
            value={stream.doc ?? emptyDoc}
            streamContent={stream.doc}
            onChange={() => {
              /* no-op: editor is locked during stream */
            }}
            context={streamCtx}
            editable={false}
            workspaceId={workspaceId}
            placeholder="Excerptus AI writing…"
          />
        ) : (
          <BriefEditor
            value={doc}
            onChange={handleChange}
            context={ctx}
            placeholder="Start writing — your text is kept locally until you Save as Draft."
            workspaceId={workspaceId}
          />
        )}
      </div>
    </div>
  );

};
